import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Optional,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
  Validators,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription, pipe, zip, Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

import {
  LocalStorageKeys,
  BomApiService,
  OptionInterface,
  SnackBarConfig,
  DecimalConfig,
} from '../../../shared';
import { trackByValue, trackByControlValue } from '../../../shared/util';
import { IBom, IBomItem } from '../../interfaces/bom.interface';

import { PricingApiService } from './../../../shared/services/api/pricing/pricing-api.service';

@Component({
  selector: 'lum-bom-form',
  templateUrl: './bom-form.component.html',
  styleUrls: ['./bom-form.component.scss'],
})
export class BomFormComponent implements OnInit, OnDestroy {
  bomId :number = 0;

  bom :IBom;

  submittingForm :boolean = false;
  loading :boolean = false;

  form :UntypedFormGroup;

  trackByValue = trackByValue;
  trackByControlValue = trackByControlValue;

  private onDestroy$ = new Subject();

  constructor(
    private bomApiService :BomApiService,
    private route :ActivatedRoute,
    private router :Router,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    private pricingApiService :PricingApiService,
    @Optional() public dialogRef :MatDialogRef<BomFormComponent>,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.getRouterParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  drop(event :CdkDragDrop<string[]>) :void {
    const { previousIndex, currentIndex } = event;
    const control = this.itemsFormArray.at(previousIndex);
    this.itemsFormArray.removeAt(previousIndex);
    this.itemsFormArray.insert(currentIndex, control);

    for (let i = 0; i < this.itemsFormArray.controls.length; i++) {
      const itemControl = this.itemsFormArray.at(i) as UntypedFormGroup;
      itemControl.get('order')?.setValue(i);
    }
  }

  markFormArrayAsTouched(formArray :UntypedFormArray) :void {
    formArray.controls.forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  markFormGroupAsTouched(formGroup :UntypedFormGroup) :void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  emptyStringValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      return control.value && control.value.trim() === ''
        ? { emptyString: true }
        : null;
    };
  }

  negativeValueValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      return control.value < 0 ? { negativeValue: true } : null;
    };
  }

  duplicateRfqUomValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      const items = control.value;
      const filteredItems = items.filter((item :any) => item.rfqId && item.uomId);
      const rfqUomCombos = filteredItems.map(
        (item :any) => `${item.rfqId}-${item.uomId}`,
      );
      const hasDuplicates = rfqUomCombos.some(
        (val :string, i :number) => rfqUomCombos.indexOf(val) !== i,
      );

      if (hasDuplicates) {
        return { duplicateRfqUom: true };
      }

      return null;
    };
  }

  rfqRequiredFieldsValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      const group = control as UntypedFormGroup;
      const rfqId = group.get('rfqId')?.value;
      const quantity = group.get('quantity')?.value;
      const uomId = group.get('uomId')?.value;

      if (!rfqId) {
        return null;
      }

      if (quantity === null || quantity === undefined) {
        return { requiredQuantity: true };
      }

      if (quantity < 0) {
        return { negativeQuantity: true };
      }

      if (!uomId) {
        return { requiredUom: true };
      }

      return null;
    };
  }

  onQuantityChange(quantity :string, item :UntypedFormGroup) :void {
    item.patchValue({ quantity: Number(quantity) });
    this.#getCalculatePrice(item);
  }

  onUnitChange(event :MatSelectChange, item :UntypedFormGroup) :void {
    item.patchValue({ uomId: event.value });
    this.#getCalculatePrice(item);
  }

  onSubmit() :void {
    this.form.markAllAsTouched();

    const itemsFormArray = this.form.get('items');
    if (itemsFormArray && itemsFormArray instanceof UntypedFormArray) {
      this.markFormArrayAsTouched(itemsFormArray);
    }

    if (!this.form.value.items.some((item :any) => item.rfqId)) {
      this.snackBar.open('At least one item is required.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    const itemsErrors = this.form.get('items')?.errors;
    if (itemsErrors && itemsErrors['duplicateRfqUom']) {
      this.snackBar.open(
        'SKU with the same unit of measure has been added multiple times. Please remove or change the duplicates.',
        'Fail',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );

      return;
    }

    const items = this.form.value.items
      .filter((item :any) => item.rfqId)
      .map((item :any, index :number) => {
        const { sku, ...rest } = item;

        return {
          ...rest,
          order: index,
        };
      });

    for (const item of items) {
      if (item.quantity < 0) {
        this.snackBar.open('Quantity should not be less than zero', 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });

        return;
      }

      if (item.quantity === null || item.quantity === undefined) {
        this.snackBar.open('Quantity field is required', 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });

        return;
      }

      if (!item.uomId) {
        this.snackBar.open('Unit of measure is required', 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });

        return;
      }
    }

    if (this.form.invalid) {
      return;
    }

    this.submittingForm = true;

    try {
      if (!this.bomId) {
        this.bomApiService
          .create({
            name: this.form.value.name,
            items: items,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open(
                'Bill of Materials successfully added.',
                'Success',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );

              if (this.isDialog) {
                this.dialogRef.close(response);
              } else {
                this.router.navigate(['bom']);
                this.cd.detectChanges();
              }
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      } else {
        this.bomApiService
          .update(this.bomId, {
            name: this.form.value.name,
            items: items,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open(
                'Bill of Materials successfully updated.',
                'Success',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
              this.router.navigate(['bom']);
              this.cd.detectChanges();
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      }
    } catch (error :any) {
      this.submittingForm = false;
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.cd.detectChanges();
    }
  }

  #getCalculatePrice(item :UntypedFormGroup) :any {
    this.pricingApiService
      .getByRfqV2({
        rfqId: item?.value?.rfqId,
        uomId: item?.value?.uomId,
        businessAccountId: '0',
        quantity: item?.value?.quantity,
        unitPriceReference: 'basePrice',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          item.patchValue({
            unitPrice: response?.unitPrice ? response.unitPrice : 0,
            totalPrice: response?.totalPrice ? response.totalPrice : 0,
          });
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error({ error });
        },
      });
  }

  private initForm() :void {
    this.form = this.fb.group(
      {
        name: [null, [Validators.required, this.emptyStringValidator()]],
        items: this.fb.array([], this.duplicateRfqUomValidator()),
      },
      { validators: this.itemsValidator() },
    );
  }

  private itemsValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      const group = control as UntypedFormGroup;
      const items = group.controls['items'] as UntypedFormArray;

      if (items.length === 0) {
        return { atLeastOneItemRequired: true };
      }

      return null;
    };
  }

  private setFormValue() :void {
    if (this.bom && this.bom.id) {
      this.form?.get('name')?.setValue(this.bom.name);

      if (this.bom.items) {
        const itemsArray = this.itemsFormArray;
        this.bom.items.forEach((item :IBomItem) => {
          const rfq = item.rfq;
          const unitOfMeasures = rfq?.unitOfMeasures || [];
          const convertedUomId = item.uomId || null;
          const baseUomId = unitOfMeasures.find(
            (unitOfMeasure :any) => unitOfMeasure.baseUnit === 1,
          )?.id;

          itemsArray.push(
            this.fb.group({
              id: [item.id],
              sku: [
                this.fb.group({
                  sku: rfq.internalSku,
                  productName: rfq.productName,
                  id: rfq.id,
                  unitOfMeasures: this.fb.array(unitOfMeasures),
                }),
              ],
              rfqId: [item.rfqId],
              note: [item.note],
              quantity: [
                item.quantity,
                [
                  Validators.required,
                  Validators.min(DecimalConfig.minPlaces),
                  this.negativeValueValidator(),
                ],
              ],
              order: [item.order],
              uomId: [convertedUomId, Validators.required],
              baseUomId: [baseUomId],
              unitPrice: [item?.calculatedPrice?.unitPrice || 0],
              totalPrice: [item?.calculatedPrice?.totalPrice || 0],
            }),
          );
        });
      }
      this.cd.detectChanges();
    }
  }

  private loadData(id :number) :void {
    this.loading = true;
    this.bomApiService
      .getById(id, {
        relations: 'items|items.rfq.unitOfMeasures|items.unitOfMeasure',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.bom = response;
          this.setFormValue();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
      });
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { bomId } = params;
        if (bomId) {
          this.bomId = bomId;
          this.loadData(this.bomId);
        }
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onRemoveItem(i :number) :void {
    this.itemsFormArray.removeAt(i);
  }

  onAddItem(item ?:UntypedFormGroup) :void {
    const nextOrder = this.itemsFormArray.length;
    if (item) {
      this.itemsFormArray.push(item);
    } else {
      this.itemsFormArray.push(
        this.fb.group(
          {
            id: [null],
            sku: [this.fb.group({})],
            rfqId: [null],
            note: [null],
            quantity: [1],
            order: [nextOrder],
            uomId: [null],
            baseUomId: [null],
            unitPrice: [0],
          },
          { validators: this.rfqRequiredFieldsValidator() },
        ),
      );
    }
  }

  onAddMultipleItems(rows :number) :void {
    for (let item = 0; item < rows; item++) {
      this.onAddItem();
    }
  }

  onSelectRfq({ rfq } :any, item :AbstractControl) :void {
    const control = item as UntypedFormControl;
    const unitOfMeasures = rfq.unit_of_measures || [];
    const convertedUomId = unitOfMeasures ? unitOfMeasures[0].id : null;
    const baseUomId = unitOfMeasures.find(
      (unitOfMeasure :any) => unitOfMeasure.base_unit === 1,
    )?.id;

    control.patchValue({
      rfqId: rfq.id,
      uomId: convertedUomId,
      baseUomId: baseUomId,
      sku: this.fb.group({
        sku: rfq.sku,
        productName: rfq.product_name,
        id: rfq.id,
        unitOfMeasures: this.fb.array(unitOfMeasures),
      }),
      unitPrice: rfq?.base_price?.computed_weighted,
    });

    const quantityControl = control.get('quantity');
    const uomIdControl = control.get('uomId');

    if (quantityControl && uomIdControl) {
      if (rfq.id) {
        quantityControl.setValidators([
          Validators.required,
          Validators.min(DecimalConfig.minPlaces),
          this.negativeValueValidator(),
        ]);
        uomIdControl.setValidators([Validators.required]);
      } else {
        quantityControl.clearValidators();
        uomIdControl.clearValidators();
      }
      quantityControl.updateValueAndValidity();
      uomIdControl.updateValueAndValidity();
    }
  }

  get itemsControls() :AbstractControl[] {
    const control = this.form.get('items');

    return control instanceof UntypedFormArray ? control.controls : [];
  }

  get itemsFormArray() :UntypedFormArray {
    return this.form.get('items') as UntypedFormArray;
  }

  get isDialog() :boolean {
    return this.dialogRef?.componentInstance === this;
  }
}
